// apps/web/src/components/social-accounts/SocialAccountsManager.tsx
//
// Connected social account management: the generic social-accounts
// table (manual platform + handle entries) and the dedicated
// Instagram Business Login flow. The two live side by side on
// this page so the creator can self-serve link/unlink without
// having to bounce to a separate settings panel.

import { useEffect, useState } from 'react';
import { ManagerPage } from '../platform/ManagerPage.js';
import { InstagramLink } from './InstagramLink.js';
import { apiGet } from '../../lib/apiClient.js';
import type { SocialAccount } from '../../types/platform.js';

interface Creator {
  id: string;
  displayName: string;
}

function readFirstCreatorId(): string | null {
  try {
    return window.localStorage.getItem('bumbum:lastCreatorId');
  } catch {
    return null;
  }
}

function writeFirstCreatorId(id: string) {
  try {
    window.localStorage.setItem('bumbum:lastCreatorId', id);
  } catch {
    /* ignore */
  }
}

export function SocialAccountsManager() {
  const [creators, setCreators] = useState<Creator[] | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiGet<{ creators: Creator[] }>('/api/v1/creators');
        if (cancelled) return;
        const list = (res.data.creators ?? []).filter((c) => c.id);
        setCreators(list);
        const stored = readFirstCreatorId();
        const next = (stored && list.find((c) => c.id === stored)?.id) || list[0]?.id || null;
        setActiveId(next);
        if (next) writeFirstCreatorId(next);
      } catch {
        if (!cancelled) setCreators([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const active = creators?.find((c) => c.id === activeId) ?? null;

  return (
    <div className="social-accounts-manager">
      <ManagerPage
        title="Social Accounts"
        listPath="/api/v1/social-accounts"
        listKey="accounts"
        renderItem={(raw) => {
          const s = raw as SocialAccount;
          return (
            <div className="manager-card" key={s.id}>
              <h3>{s.platform} · {s.handle ?? '—'}</h3>
              <p className="status-badge">{s.status}</p>
              <p>{s.connectedAt ? `Connected ${s.connectedAt}` : 'Disconnected'}</p>
              <div className="actions">
                <button className="btn btn-secondary">Metrics</button>
                <button className="btn btn-primary">Refresh</button>
                <button className="btn btn-danger">Disconnect</button>
              </div>
            </div>
          );
        }}
      />
      {creators && creators.length > 1 && (
        <section className="manager-card form-card">
          <h3>Switch creator profile</h3>
          <select value={activeId ?? ''} onChange={(e) => { setActiveId(e.target.value); writeFirstCreatorId(e.target.value); }}>
            {creators.map((c) => <option key={c.id} value={c.id}>{c.displayName}</option>)}
          </select>
        </section>
      )}
      {creators && creators.length === 0 && (
        <section className="manager-card form-card">
          <h3>Instagram</h3>
          <p className="muted">Create a creator profile first to link an Instagram Professional account.</p>
        </section>
      )}
      {active && (
        <InstagramLink
          creatorId={active.id}
          creatorDisplayName={active.displayName}
        />
      )}
    </div>
  );
}
